import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Bell, Phone, Calendar, CheckCheck, User, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminNotifications = () => {
  const { token } = useAdminAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'

  const fetchNotifications = async () => {
    try {
      const params = filter === 'unread' ? '?unreadOnly=true' : '?limit=100';
      const res = await axios.get(`${API_URL}/api/admin/notifications${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data.notifications || []);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [token, filter]);

  const markAsRead = async (bookingId) => {
    try {
      await axios.put(`${API_URL}/api/admin/notifications/${bookingId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API_URL}/api/admin/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('pl-PL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread') return !n.isRead;
    if (filter === 'read') return n.isRead;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.isRead).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Bell className="w-7 h-7 text-purple-400" />
            Powiadomienia
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-sm px-2 py-0.5 rounded-full">
                {unreadCount} nowych
              </span>
            )}
          </h1>
          <p className="text-gray-400 mt-1">
            Powiadomienia o brakujących danych instruktorów
          </p>
        </div>
        
        {unreadCount > 0 && (
          <Button
            onClick={markAllAsRead}
            variant="outline"
            className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
          >
            <CheckCheck className="w-4 h-4 mr-2" />
            Oznacz wszystkie jako przeczytane
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {[
          { value: 'all', label: 'Wszystkie' },
          { value: 'unread', label: 'Nieprzeczytane' },
          { value: 'read', label: 'Przeczytane' }
        ].map(f => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`mb-8 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === f.value
                ? 'bg-purple-500 text-white'
                : 'bg-zinc-800 text-gray-400 hover:text-white hover:bg-zinc-700'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Notifications List */}
      {filteredNotifications.length === 0 ? (
        <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-12 text-center">
          <Bell className="w-12 h-12 mx-auto mb-4 text-gray-600" />
          <h3 className="text-lg font-medium text-white mb-2">Brak powiadomień</h3>
          <p className="text-gray-400">
            {filter === 'unread' 
              ? 'Wszystkie powiadomienia zostały przeczytane'
              : 'Nie ma jeszcze żadnych powiadomień'
            }
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredNotifications.map((notif, index) => (
            <div
              key={notif.bookingId || index}
              className={`mb-4 bg-zinc-900/50 border rounded-xl p-4 transition-all ${
                !notif.isRead 
                  ? 'border-purple-500/50 bg-purple-500/5' 
                  : 'border-white/10'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-xl ${
                  notif.type === 'instructor_no_phone' 
                    ? 'bg-orange-500/20 text-orange-400' 
                    : 'bg-blue-500/20 text-blue-400'
                }`}>
                  {notif.type === 'instructor_no_phone' ? (
                    <Phone className="w-5 h-5" />
                  ) : (
                    <Calendar className="w-5 h-5" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-white font-semibold flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        {notif.instructorName}
                        {!notif.isRead && (
                          <span className="bg-purple-500 text-white text-xs px-2 py-0.5 rounded-full">
                            Nowe
                          </span>
                        )}
                      </h3>
                      <p className="text-gray-400 text-sm mt-1">
                        {notif.type === 'instructor_no_phone' 
                          ? 'Instruktor nie ma przypisanego numeru telefonu do powiadomień SMS'
                          : notif.message
                        }
                      </p>
                    </div>
                    
                    {!notif.isRead && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-gray-400 hover:text-white"
                        onClick={() => markAsRead(notif.bookingId)}
                      >
                        <CheckCheck className="w-4 h-4" />
                      </Button>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-gray-500">
                    <span>Rezerwacja: {notif.bookingId}</span>
                    <span>ID instruktora: {notif.instructorId}</span>
                    <span>{formatDate(notif.createdAt)}</span>
                  </div>

                  {notif.type === 'instructor_no_phone' && (
                    <div className="mt-3">
                      <a
                        href="/admin/instructors"
                        className="inline-flex items-center gap-1 text-sm text-purple-400 hover:text-purple-300"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Dodaj numer w panelu Pracownicy
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminNotifications;
